import { create } from 'zustand'
import type { Table, Relation } from '@/types'
import { useDiagramStore } from './diagramStore'
import { useSelectionStore } from './selectionStore'
import { genId } from '@/utils/id'
import { newUid } from '@/utils/uid'
import { buildFullTableName } from '@/utils/normalizers'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ClipboardState {
  copiedTables: Table[]
  copiedRels:   Relation[]

  /**
   * Copy tables by ID from the current diagram state.
   * Also copies relationships where both endpoints are among the copied tables.
   */
  copyTables: (ids: string[]) => void

  /**
   * Paste the copied tables (and their internal relationships) into the diagram.
   * All pasted tables + relationships are committed as a single undo entry.
   *
   * @param position - canvas coordinate where to place the first pasted table.
   *   If multiple tables are pasted, the relative offsets between them are preserved.
   *   If undefined, a +32px offset from the original positions is used.
   */
  pasteTables: (position?: { x: number; y: number }) => void
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useClipboardStore = create<ClipboardState>()((set, get) => ({
  copiedTables: [],
  copiedRels:   [],

  copyTables: (ids) => {
    const { tables, rels } = useDiagramStore.getState()
    const idSet = new Set(ids)

    const copiedTables = tables
      .filter((t) => idSet.has(t.id))
      .map((t) => structuredClone(t))

    const copiedRels = rels
      .filter((r) => idSet.has(r.from) && idSet.has(r.to))
      .map((r) => structuredClone(r))

    set({ copiedTables, copiedRels })
  },

  pasteTables: (position) => {
    const { copiedTables, copiedRels } = get()
    if (!copiedTables.length) return

    const { tables, pasteTablesAndRels } = useDiagramStore.getState()
    const { selectMultipleTables } = useSelectionStore.getState()

    // Build ID mapping: original table id → new UUID. Unicidade no NOME (fullName).
    const existingFullNames = new Set(tables.map((t) => t.fullName))
    const reservedFullNames = new Set<string>()
    const firstSrc    = copiedTables[0]
    const idMap: Record<string, string> = {}

    const scheduled: Array<{
      src: Table
      newId: string
      newName: string
      newFullName: string
      newSchema: string
      x: number
      y: number
    }> = []

    for (const src of copiedTables) {
      const baseName = src.name.replace(/_copy(_\d+)?$/, '') || 'table'
      let candidateName = `${baseName}_copy`
      let counter = 1
      let candidateFullName = buildFullTableName(src.schema, candidateName) || candidateName
      while (existingFullNames.has(candidateFullName) || reservedFullNames.has(candidateFullName)) {
        candidateName = `${baseName}_copy_${counter++}`
        candidateFullName = buildFullTableName(src.schema, candidateName) || candidateName
      }
      reservedFullNames.add(candidateFullName)
      const newId = newUid()
      idMap[src.id] = newId

      const x = position ? position.x + (src.x - firstSrc.x) : src.x + 32
      const y = position ? position.y + (src.y - firstSrc.y) : src.y + 32
      scheduled.push({
        src,
        newId,
        newName: candidateName,
        newFullName: candidateFullName,
        newSchema: src.schema,
        x,
        y,
      })
    }

    const newTables: Table[] = scheduled.map(({ src, newId, newName, newFullName, newSchema, x, y }) => {
      const clone = structuredClone(src)
      return {
        ...clone,
        id:       newId,
        name:     newName,
        schema:   newSchema,
        fullName: newFullName,
        // Cópias têm colunas distintas → ids novos.
        cols:     clone.cols.map((c) => ({ ...c, id: newUid() })),
        x,
        y,
      }
    })

    const newTableMap = new Map(newTables.map((table) => [table.id, table]))

    const newRels: Relation[] = copiedRels
      .filter((r) => idMap[r.from] !== undefined && idMap[r.to] !== undefined)
      .map((r) => ({
        ...structuredClone(r),
        id:   genId('rel'),
        from: idMap[r.from]!,
        to:   idMap[r.to]!,
        fromTableName: newTableMap.get(idMap[r.from]!)?.fullName ?? r.fromTableName,
        toTableName: newTableMap.get(idMap[r.to]!)?.fullName ?? r.toTableName,
        fromSchema: newTableMap.get(idMap[r.from]!)?.schema ?? r.fromSchema,
        toSchema: newTableMap.get(idMap[r.to]!)?.schema ?? r.toSchema,
      }))

    pasteTablesAndRels(newTables, newRels)
    selectMultipleTables(scheduled.map((s) => s.newId))
  },
}))
